import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Clock,
  MapPin,
  User,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Scissors,
  Wrench,
  Award,
  CalendarClock,
  ClipboardCheck,
  LogOut,
  ShieldAlert,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  useAssetInspections,
  useAssetHistory,
  useAssetRopeCuts,
  useAssetMaintenance,
  useAssetCertifications,
  useAssetReservations,
  useAssetProblemReports,
} from '@/hooks/use-assets';
import { useOrgTimeZone, useOrgToday } from '@/hooks/use-settings';
import { usePermissions } from '@/hooks/use-permissions';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import type {
  Asset,
  Inspection,
  Deployment,
  RopeCut,
  MaintenanceRecord,
  Certification,
  AssetReservation,
  AssetProblemReport,
} from '@/lib/types';

interface AssetTabsProps {
  asset: Asset;
  preselectedTab?: string;
  readOnly?: boolean;
}

export function AssetTabs({ asset, preselectedTab, readOnly = false }: AssetTabsProps) {
  const [activeTab, setActiveTab] = useState(preselectedTab || 'overview');
  const listWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listWrapperRef.current?.querySelector<HTMLElement>('[data-state="active"]');
    el?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, []);

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <div ref={listWrapperRef} className="overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
      <TabsList className="flex w-full lg:grid lg:grid-cols-8">
        <TabsTrigger value="overview" className="whitespace-nowrap">Overview</TabsTrigger>
        <TabsTrigger value="inspections" className="whitespace-nowrap">Inspections</TabsTrigger>
        <TabsTrigger value="deployments" className="whitespace-nowrap">Deployments</TabsTrigger>
        <TabsTrigger value="rope-cuts" className="whitespace-nowrap">Rope Cuts</TabsTrigger>
        <TabsTrigger value="maintenance" className="whitespace-nowrap">Maintenance</TabsTrigger>
        <TabsTrigger value="certifications" className="whitespace-nowrap">Certifications</TabsTrigger>
        <TabsTrigger value="reservations" className="whitespace-nowrap">Reservations</TabsTrigger>
        <TabsTrigger value="problem-reports" className="whitespace-nowrap">Problem Reports</TabsTrigger>
      </TabsList>
      </div>

      <TabsContent value="overview" className="mt-6">
        <OverviewTab asset={asset} />
      </TabsContent>

      <TabsContent value="inspections" className="mt-6">
        <InspectionsTab assetId={asset.id} readOnly={readOnly} />
      </TabsContent>

      <TabsContent value="deployments" className="mt-6">
        <DeploymentsTab assetId={asset.id} assetStatus={asset.status} readOnly={readOnly} />
      </TabsContent>

      <TabsContent value="rope-cuts" className="mt-6">
        <RopeCutsTab assetId={asset.id} />
      </TabsContent>

      <TabsContent value="maintenance" className="mt-6">
        <MaintenanceTab assetId={asset.id} />
      </TabsContent>

      <TabsContent value="certifications" className="mt-6">
        <CertificationsTab assetId={asset.id} />
      </TabsContent>

      <TabsContent value="reservations" className="mt-6">
        <ReservationsTab assetId={asset.id} />
      </TabsContent>

      <TabsContent value="problem-reports" className="mt-6">
        <ProblemReportsTab assetId={asset.id} />
      </TabsContent>
    </Tabs>
  );
}

function OverviewTab({ asset }: { asset: Asset }) {
  const orgTimeZone = useOrgTimeZone();
  const orgToday = useOrgToday();
  const inspectionOverdue =
    asset.next_inspection_due && asset.next_inspection_due.slice(0, 10) < orgToday;

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <InfoRow label="Type" value={asset.type ? asset.type.replace('_', ' ') : '-'} />
          <InfoRow label="Serial Number" value={asset.serial_number} />
          <InfoRow label="Status" value={<StatusBadge status={asset.status} />} />
          <InfoRow label="Manufacturer" value={asset.manufacturer || '-'} />
          <InfoRow label="Model" value={asset.model || '-'} />
          <InfoRow
            label="Date of Manufacture"
            value={formatDateOnly(asset.date_of_manufacture, '-')}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Specifications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <InfoRow label="Length" value={asset.length_m ? `${asset.length_m}m` : '-'} />
          <InfoRow
            label="Safe Working Load"
            value={asset.safe_working_load ? `${asset.safe_working_load} kg` : '-'}
          />
          <InfoRow
            label="Inspection Interval"
            value={asset.inspection_interval_days ? `${asset.inspection_interval_days} days` : '-'}
          />
          <InfoRow
            label="Current Location"
            value={asset.location_name || (asset.current_location ? `Location #${asset.current_location}` : '-')}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Inspection Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <InfoRow
            label="Next Inspection Due"
            value={
              asset.next_inspection_due ? (
                <span
                  className={inspectionOverdue ? 'text-red-600 font-medium' : ''}
                >
                  {formatDateOnly(asset.next_inspection_due, '-')}
                  {inspectionOverdue && ' (Overdue)'}
                </span>
              ) : (
                '-'
              )
            }
          />
          <InfoRow
            label="Overdue"
            value={
              asset.is_overdue ? (
                <Badge variant="destructive">Yes</Badge>
              ) : (
                <Badge variant="secondary">No</Badge>
              )
            }
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Dates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <InfoRow
            label="Date Added"
            value={formatDateTimeInTimeZone(asset.date_added, orgTimeZone, {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
            })}
          />
          <InfoRow
            label="Purchase Date"
            value={formatDateOnly(asset.purchase_date, '-')}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function InspectionsTab({ assetId, readOnly }: { assetId: number; readOnly: boolean }) {
  const { data: inspections, isLoading, isError } = useAssetInspections(assetId);
  const { canRecordInspections } = usePermissions();

  let content: React.ReactNode;
  if (isLoading) {
    content = <LoadingSkeleton />;
  } else if (isError) {
    content = <ErrorMessage message="Failed to load inspections" />;
  } else if (!inspections || inspections.length === 0) {
    content = <EmptyState message="No inspections recorded" icon={CheckCircle} />;
  } else {
    content = (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Result</TableHead>
                <TableHead>Findings</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inspections.map((inspection: Inspection) => (
                <TableRow key={inspection.id}>
                  <TableCell>
                    <span className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      {formatDateOnly(inspection.date, '-')}
                    </span>
                  </TableCell>
                  <TableCell>
                    <InspectionResultBadge result={inspection.result} />
                  </TableCell>
                  <TableCell>{inspection.findings?.length || 0} finding(s)</TableCell>
                  <TableCell className="max-w-xs truncate">{inspection.notes || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {!readOnly && (
        <div className="flex justify-end">
          {canRecordInspections ? (
            <Button asChild>
              <Link to={`/inspections/new?asset=${assetId}`}>
                <ClipboardCheck className="h-4 w-4 mr-2" />
                Begin Inspection
              </Link>
            </Button>
          ) : (
            <Button
              disabled
              title="Requires Inspection Certified — an admin can enable it on your tech profile"
            >
              <ClipboardCheck className="h-4 w-4 mr-2" />
              Begin Inspection
            </Button>
          )}
        </div>
      )}

      {content}
    </div>
  );
}

function DeploymentsTab({
  assetId,
  assetStatus,
  readOnly,
}: {
  assetId: number;
  assetStatus: string;
  readOnly: boolean;
}) {
  const orgTimeZone = useOrgTimeZone();
  const { canManageGear } = usePermissions();
  const canDeploy =
    canManageGear && (assetStatus === 'available' || assetStatus === 'active');
  const { data: deployments, isLoading, isError } = useAssetHistory(assetId);

  let content: React.ReactNode;
  if (isLoading) {
    content = <LoadingSkeleton />;
  } else if (isError) {
    content = <ErrorMessage message="Failed to load deployment history" />;
  } else if (!deployments || deployments.length === 0) {
    content = <EmptyState message="No deployment history" icon={MapPin} />;
  } else {
    content = (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Action</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deployments.map((deployment: Deployment) => (
                <TableRow key={deployment.id}>
                  <TableCell className="capitalize">{deployment.action.replace('_', ' ')}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      {formatDateTimeInTimeZone(deployment.timestamp, orgTimeZone, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                  </TableCell>
                  <TableCell>
                    {formatDateOnly(deployment.due_date, '-')}
                  </TableCell>
                  <TableCell>
                    <DeploymentStatusBadge status={deployment.approval_status} />
                  </TableCell>
                  <TableCell className="max-w-xs truncate">{deployment.notes || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {canDeploy && !readOnly && (
        <div className="flex justify-end">
          <Button asChild>
            <Link to={`/deployments/check-out?asset=${assetId}`}>
              <LogOut className="h-4 w-4 mr-2" />
              Deploy Asset
            </Link>
          </Button>
        </div>
      )}

      {content}
    </div>
  );
}

function RopeCutsTab({ assetId }: { assetId: number }) {
  const orgTimeZone = useOrgTimeZone();
  const { data: ropeCuts, isLoading, isError } = useAssetRopeCuts(assetId);

  if (isLoading) return <LoadingSkeleton />;
  if (isError) return <ErrorMessage message="Failed to load rope cuts" />;
  if (!ropeCuts || ropeCuts.length === 0) return <EmptyState message="No rope cuts recorded" icon={Scissors} />;

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Cut Length</TableHead>
              <TableHead>Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ropeCuts.map((cut: RopeCut) => (
              <TableRow key={cut.id}>
                <TableCell>
                  <span className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    {formatDateTimeInTimeZone(cut.created_at, orgTimeZone, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </TableCell>
                <TableCell className="font-medium">{cut.cut_length}m</TableCell>
                <TableCell className="max-w-xs truncate">{cut.notes || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function MaintenanceTab({ assetId }: { assetId: number }) {
  const { data: records, isLoading, isError } = useAssetMaintenance(assetId);

  if (isLoading) return <LoadingSkeleton />;
  if (isError) return <ErrorMessage message="Failed to load maintenance records" />;
  if (!records || records.length === 0) return <EmptyState message="No maintenance records" icon={Wrench} />;

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Performed By</TableHead>
              <TableHead>Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.map((record: MaintenanceRecord) => (
              <TableRow key={record.id}>
                <TableCell>
                  <span className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    {formatDateOnly(record.date, '-')}
                  </span>
                </TableCell>
                <TableCell className="capitalize">{record.type.replace('_', ' ')}</TableCell>
                <TableCell>
                  <span className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    {record.performed_by_username || '-'}
                  </span>
                </TableCell>
                <TableCell className="max-w-xs truncate">{record.notes || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function CertificationsTab({ assetId }: { assetId: number }) {
  const { data: certifications, isLoading, isError } = useAssetCertifications(assetId);

  if (isLoading) return <LoadingSkeleton />;
  if (isError) return <ErrorMessage message="Failed to load certifications" />;
  if (!certifications || certifications.length === 0) return <EmptyState message="No certifications" icon={Award} />;

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Issuer</TableHead>
              <TableHead>Issue Date</TableHead>
              <TableHead>Expiry Date</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {certifications.map((cert: Certification) => (
              <TableRow key={cert.id}>
                <TableCell className="font-medium">{cert.cert_type}</TableCell>
                <TableCell>{cert.issuer || '-'}</TableCell>
                <TableCell>{formatDateOnly(cert.issue_date, '-')}</TableCell>
                <TableCell>
                  <span className={cert.is_expired ? 'text-red-600' : ''}>
                    {formatDateOnly(cert.expiry_date, '-')}
                  </span>
                </TableCell>
                <TableCell>
                  {cert.is_expired ? (
                    <Badge variant="destructive">Expired</Badge>
                  ) : (
                    <Badge variant="secondary">Active</Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function ReservationsTab({ assetId }: { assetId: number }) {
  const { data: reservations, isLoading, isError } = useAssetReservations(assetId);

  if (isLoading) return <LoadingSkeleton />;
  if (isError) return <ErrorMessage message="Failed to load reservations" />;
  if (!reservations || reservations.length === 0) return <EmptyState message="No reservations" icon={CalendarClock} />;

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reserved By</TableHead>
              <TableHead>From</TableHead>
              <TableHead>Until</TableHead>
              <TableHead>Purpose</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reservations.map((reservation: AssetReservation) => (
              <TableRow key={reservation.id}>
                <TableCell>
                  <span className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    {reservation.user_name || '-'}
                  </span>
                </TableCell>
                <TableCell>{formatDateOnly(reservation.reserved_from, '-')}</TableCell>
                <TableCell>{formatDateOnly(reservation.reserved_until, '-')}</TableCell>
                <TableCell className="max-w-xs truncate">{reservation.purpose || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function ProblemReportsTab({ assetId }: { assetId: number }) {
  const orgTimeZone = useOrgTimeZone();
  const { data: reports, isLoading, isError } = useAssetProblemReports(assetId);

  if (isLoading) return <LoadingSkeleton />;
  if (isError) return <ErrorMessage message="Failed to load problem reports" />;
  if (!reports || reports.length === 0) {
    return <EmptyState message="No problems reported" icon={ShieldAlert} />;
  }

  return (
    <div className="space-y-3">
      {reports.map((report: AssetProblemReport) => (
        <Card key={report.id}>
          <CardContent className="p-4 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
              <span className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-400" />
                {report.reporter_name || 'Unknown'}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-400" />
                {formatDateTimeInTimeZone(report.created_at, orgTimeZone, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
            <p className="whitespace-pre-wrap text-gray-900">{report.description}</p>
            <p className="text-sm text-gray-600">
              {report.status_changed
                ? `Status: ${report.previous_status} → ${report.new_status}`
                : `Status unchanged (${report.new_status})`}
            </p>
            {report.photo_url && (
              <a href={report.photo_url} target="_blank" rel="noopener noreferrer">
                <img
                  src={report.photo_url}
                  alt="Problem report photo"
                  className="h-32 w-32 rounded object-cover"
                />
              </a>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-gray-600">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    available: 'bg-green-100 text-green-800',
    deployed: 'bg-blue-100 text-blue-800',
    in_use: 'bg-blue-100 text-blue-800',
    damaged: 'bg-orange-100 text-orange-800',
    retired: 'bg-gray-100 text-gray-800',
    lost: 'bg-red-100 text-red-800',
  };
  return <Badge className={colors[status] || 'bg-gray-100 text-gray-800'}>{status}</Badge>;
}

function InspectionResultBadge({ result }: { result: string | null }) {
  if (result === 'pass') {
    return (
      <Badge className="bg-green-100 text-green-800">
        <CheckCircle className="h-3 w-3 mr-1" />
        Pass
      </Badge>
    );
  }
  if (result === 'fail') {
    return (
      <Badge className="bg-red-100 text-red-800">
        <XCircle className="h-3 w-3 mr-1" />
        Fail
      </Badge>
    );
  }
  return (
    <Badge className="bg-yellow-100 text-yellow-800">
      <AlertTriangle className="h-3 w-3 mr-1" />
      Conditional
    </Badge>
  );
}

function DeploymentStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  };
  return <Badge className={colors[status] || 'bg-gray-100 text-gray-800'}>{status}</Badge>;
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className="h-16 w-full" />
      ))}
    </div>
  );
}

function ErrorMessage({ message }: { message: string }) {
  return (
    <div className="p-6 text-center text-red-600">
      <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
      {message}
    </div>
  );
}

function EmptyState({ message, icon: Icon }: { message: string; icon: React.ComponentType<{ className?: string }> }) {
  return (
    <div className="p-12 text-center text-gray-500">
      <Icon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
      <p>{message}</p>
    </div>
  );
}
